import {faPlus} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Table} from '@mantine/core';
import {FC, useEffect} from 'react';
import {ActionCodesService} from '../../../api/generated/ActionCodesService';
import {CodesService} from '../../../api/generated/CodesService';
import {Error, PartItemDetailDto} from '../../../api/generated/index.defs';
import {ProtectedElement} from '../../../auth/require-authorization';
import {useOptions} from '../../../hooks/use-options';
import {useJobStore} from '../../job-store';
import classes from '../job-evaluation.module.css';
import {MmJobPartItemRow, NonMmJobPartItemRow} from './part-item-row';
import {EditPartItemRoles, PartsListForm} from './parts-list';
import {usePartsListStore} from './parts-list-store';
import {useFormContext, FieldPath} from 'react-hook-form';
import {JobPartsListSchema} from './schema';

type PartListTableRowsProps = {
  partKitNumber?: string;
  isReadonly?: boolean;
  fieldErrors: Error[];
};

export const MmJobPartListRows: FC<PartListTableRowsProps> = ({
  partKitNumber,
  fieldErrors,
  isReadonly,
}) => {
  const {setError} = useFormContext<JobPartsListSchema>();

  useEffect(() => {
    if (fieldErrors.length > 0) {
      fieldErrors.forEach((error) => {
        setError(error.propertyName as FieldPath<JobPartsListSchema>, {
          type: 'manual',
          message: error.errorMessage,
        });
      });
    }
  }, [fieldErrors, setError]);

  const actionCodeOptions =
    useOptions(ActionCodesService.getActionCodeOptions).value ?? [];
  const dispositionCodeOptions =
    useOptions(CodesService.getDispositionCodeOptions).value ?? [];

  const actionCodesIdsWithDispositionRequirement: number[] = actionCodeOptions
    .filter((item) => item.meta?.requireDisposition)
    .map((item) => item.meta.id);

  const defaultActionCode = actionCodeOptions.find(
    (item) => item.meta.isDefault,
  );

  const {isJobReadonly} = useJobStore((state) => ({
    isJobReadonly: state.isJobReadonly,
  }));

  const {showAdminFeatures} = usePartsListStore((state) => ({
    showAdminFeatures: state.showAdminFeatures,
  }));

  return (
    <>
      <PartsListForm.FieldArray name='partItems'>
        {({fields, append, update, move, moveUp, moveDown, remove}) => (
          <>
            {fields.map((partItemField, index) => (
              <MmJobPartItemRow
                key={`partItem[${index}]-${partItemField?.id}`}
                move={move}
                remove={remove}
                update={update}
                moveUp={moveUp}
                moveDown={moveDown}
                index={index}
                isReadonly={isReadonly}
                fieldsLength={fields.length}
                partKitNumber={partKitNumber}
                actionCodeOptions={actionCodeOptions}
                dispositionCodeOptions={dispositionCodeOptions}
                partItemField={partItemField as unknown as PartItemDetailDto}
                actionCodesIdsWithDispositionRequirement={
                  actionCodesIdsWithDispositionRequirement
                }
              />
            ))}
            <ProtectedElement role={EditPartItemRoles}>
              {showAdminFeatures && (
                <Table.Tr key='add-part-item' hidden={isJobReadonly}>
                  <Table.Td p={0} colSpan={9}>
                    <Button
                      h={20}
                      size='xs'
                      fullWidth
                      variant='outline'
                      className={classes.addPartButton}
                      leftSection={<FontAwesomeIcon icon={faPlus} />}
                      onClick={() =>
                        append({
                          qty: 1,
                          class: '',
                          partNumber: '',
                          description: '',
                          dispositionCodeId: 0,
                          materialSequence: '0',
                          canBeChanged: true,
                          actionCodeId: defaultActionCode?.meta.id,
                        })
                      }
                    >
                      Add Part
                    </Button>
                  </Table.Td>
                </Table.Tr>
              )}
            </ProtectedElement>
          </>
        )}
      </PartsListForm.FieldArray>
    </>
  );
};

export const NonMmJobPartListRows: FC<PartListTableRowsProps> = ({
  partKitNumber,
  fieldErrors,
  isReadonly,
}) => {
  const {setError} = useFormContext<JobPartsListSchema>();

  useEffect(() => {
    if (fieldErrors.length > 0) {
      fieldErrors.forEach((error) => {
        setError(error.propertyName as FieldPath<JobPartsListSchema>, {
          type: 'manual',
          message: error.errorMessage,
        });
      });
    }
  }, [fieldErrors, setError]);

  const {isJobReadonly} = useJobStore((state) => ({
    isJobReadonly: state.isJobReadonly,
  }));

  return (
    <>
      <PartsListForm.FieldArray name='partItems'>
        {({fields, append, update, move, moveUp, moveDown, remove}) => (
          <>
            {fields.map((partItemField, index) => (
              <NonMmJobPartItemRow
                key={`partItem[${index}]-${partItemField?.id}`}
                move={move}
                remove={remove}
                update={update}
                moveUp={moveUp}
                moveDown={moveDown}
                index={index}
                isReadonly={isReadonly}
                fieldsLength={fields.length}
                partKitNumber={partKitNumber}
                partItemField={partItemField as unknown as PartItemDetailDto}
              />
            ))}
            <Table.Tr key='add-part-item' hidden={isJobReadonly}>
              <Table.Td p={0} colSpan={9}>
                <Button
                  fullWidth
                  variant='outline'
                  className={classes.addPartButton}
                  leftSection={<FontAwesomeIcon icon={faPlus} />}
                  onClick={() =>
                    append({
                      qty: 1,
                    })
                  }
                >
                  Add Part
                </Button>
              </Table.Td>
            </Table.Tr>
          </>
        )}
      </PartsListForm.FieldArray>
    </>
  );
};